import { Injectable, NgZone, inject } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { Socket, io } from 'socket.io-client';

import { DeviceInfoService } from '@core/services/device-info.service';
import { environment } from '@environments/environment';
import {
  DeviceType,
  IdentifyMessage,
  SupportedLanguage,
  WsEnvelope,
} from './realtime.types';

/**
 * Lifecycle states surfaced to NgRx via the lifecycle effect.
 */
export type RealtimeConnectionState =
  | 'IDLE'
  | 'CONNECTING'
  | 'IDENTIFYING'
  | 'CONNECTED'
  | 'RECONNECTING'
  | 'DISCONNECTED'
  | 'FAILED';

export interface RealtimeConnectParams {
  accessToken: string;
  language: SupportedLanguage;
  deviceType: DeviceType;
}

const IDENTIFY_TIMEOUT_MS = 10_000;

/**
 * Singleton wrapper around `socket.io-client`. Owns one Socket.IO instance for
 * the entire app and surfaces incoming envelopes via `events$` and lifecycle
 * transitions via `state$`. Effects translate these to NgRx actions.
 *
 * Why bypass the HttpClient interceptor chain (per CLAUDE.md hard rule):
 * Socket.IO is not HTTP — there is no request the auth interceptor could
 * decorate. The access token is supplied to `connect()` from the lifecycle
 * effect, which reads it from `TokenStorageService`. Tokens never leave
 * `TokenStorageService` for any other purpose here.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private readonly zone = inject(NgZone);
  private readonly deviceInfo = inject(DeviceInfoService);

  private socket: Socket | null = null;
  private identifyTimer: ReturnType<typeof setTimeout> | null = null;
  private currentIdentify: IdentifyMessage | null = null;

  private readonly eventsSubject = new Subject<WsEnvelope>();
  private readonly stateSubject = new BehaviorSubject<RealtimeConnectionState>(
    'IDLE'
  );

  /** Server → client envelopes. Multicast subject. */
  readonly events$: Observable<WsEnvelope> = this.eventsSubject.asObservable();

  /** Connection lifecycle. Always emits the current state on subscribe. */
  readonly state$: Observable<RealtimeConnectionState> =
    this.stateSubject.asObservable();

  get state(): RealtimeConnectionState {
    return this.stateSubject.value;
  }

  connect(params: RealtimeConnectParams): void {
    if (this.socket) {
      this.disconnect('user');
    }

    this.currentIdentify = {
      accessToken: params.accessToken,
      deviceType: params.deviceType,
      language: params.language,
      ...this.deviceInfo.getClientLabels(),
    };

    this.setState('CONNECTING');

    // Socket.IO is event-loop heavy outside Angular zone; run outside to avoid
    // change-detection thrash, then re-enter the zone when emitting to NgRx.
    this.zone.runOutsideAngular(() => {
      const socket = io(environment.realtimeUrl, {
        path: environment.realtimePath,
        transports: ['websocket', 'polling'],
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1_000,
        reconnectionDelayMax: 30_000,
        timeout: 20_000,
      });

      socket.on('connect', () => this.onConnect());
      socket.on('disconnect', (reason: string) => this.onDisconnect(reason));
      socket.on('connect_error', () => this.onConnectError());
      socket.io.on('reconnect_attempt', () =>
        this.setStateInZone('RECONNECTING')
      );
      socket.on('message', (envelope: WsEnvelope) => this.onMessage(envelope));

      this.socket = socket;
      socket.connect();
    });
  }

  disconnect(
    _reason: 'user' | 'language-change' | 'token-refresh' = 'user'
  ): void {
    this.clearIdentifyTimer();
    this.currentIdentify = null;

    if (this.socket) {
      this.socket.removeAllListeners();
      try {
        this.socket.disconnect();
      } catch {
        // Socket.IO swallows transport errors; nothing useful to surface.
      }
      this.socket = null;
    }
    this.setState('IDLE');
  }

  /**
   * Mark the connection identified. Called by the lifecycle effect after the
   * server sends `IDENTIFIED`.
   */
  markIdentified(): void {
    this.clearIdentifyTimer();
    this.setStateInZone('CONNECTED');
  }

  /**
   * Mark the connection as failed (used for `IDENTIFY_FAILED` and terminal
   * disconnect reasons). Effect decides what to do next.
   */
  markFailed(): void {
    this.clearIdentifyTimer();
    this.setStateInZone('FAILED');
  }

  private onConnect(): void {
    if (!this.currentIdentify || !this.socket) {
      return;
    }
    this.setStateInZone('IDENTIFYING');
    this.socket.emit('IDENTIFY', this.currentIdentify);
    this.startIdentifyTimer();
  }

  private onDisconnect(_reason: string): void {
    this.clearIdentifyTimer();
    this.setStateInZone('DISCONNECTED');
  }

  private onConnectError(): void {
    this.clearIdentifyTimer();
    this.setStateInZone('RECONNECTING');
  }

  private onMessage(envelope: WsEnvelope): void {
    if (!envelope || typeof envelope !== 'object' || !envelope.type) {
      return;
    }
    this.zone.run(() => this.eventsSubject.next(envelope));
  }

  private startIdentifyTimer(): void {
    this.clearIdentifyTimer();
    this.identifyTimer = setTimeout(() => {
      // Server is expected to push IDENTIFY_FAILED with reason IDENTIFY_TIMEOUT,
      // but we still self-protect in case the server is unreachable mid-handshake.
      this.markFailed();
    }, IDENTIFY_TIMEOUT_MS);
  }

  private clearIdentifyTimer(): void {
    if (this.identifyTimer) {
      clearTimeout(this.identifyTimer);
      this.identifyTimer = null;
    }
  }

  private setState(next: RealtimeConnectionState): void {
    if (this.stateSubject.value !== next) {
      this.stateSubject.next(next);
    }
  }

  private setStateInZone(next: RealtimeConnectionState): void {
    this.zone.run(() => this.setState(next));
  }
}
